import { useState, useEffect } from "react";
import { Title, useDataProvider, useNotify } from "react-admin";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Check as CheckIcon,
} from "@mui/icons-material";

const COOLDOWN_DURATION_MS = 20 * 60 * 1000; // 20 minutes
const COOLDOWN_STORAGE_KEY = "statsReportGenerationTimestamp";

/**
 * Format UTC timestamp to user's local timezone
 */
const formatTimestampToLocal = (timestamp) => {
  if (!timestamp) return "-";
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const StatsReportCachePanel = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [reportsData, setReportsData] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Check cooldown status
  const checkCooldown = () => {
    const lastGenerated = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (lastGenerated) {
      const elapsed = Date.now() - parseInt(lastGenerated, 10);
      const remaining = COOLDOWN_DURATION_MS - elapsed;
      if (remaining > 0) {
        setCooldownRemaining(remaining);
        return true;
      } else {
        localStorage.removeItem(COOLDOWN_STORAGE_KEY);
        setCooldownRemaining(0);
      }
    }
    return false;
  };

  // Update cooldown timer
  useEffect(() => {
    checkCooldown();
    const interval = setInterval(() => {
      checkCooldown();
    }, 60000); // Update every 1 minute
    return () => clearInterval(interval);
  }, []);

  const fetchReportInfo = async () => {
    setLoadingInfo(true);
    try {
      const result = await dataProvider.getLatestStatsReport();
      setReportsData(result?.data?.latestStatsReport);
    } catch (error) {
      console.error("Failed to fetch report info:", error);
    } finally {
      setLoadingInfo(false);
    }
  };

  useEffect(() => {
    fetchReportInfo();
    // eslint-disable-next-line
  }, []);

  const handleGenerate = async () => {
    if (cooldownRemaining > 0) return;

    setIsGenerating(true);

    // Fire and forget - trigger the mutation without waiting for completion
    dataProvider.generateStatsReport({}).catch((error) => {
      console.error("Stats report generation error:", error);
    });

    // Wait 5 seconds to give the Lambda time to start
    await new Promise((resolve) => setTimeout(resolve, 5000));

    // Set cooldown
    localStorage.setItem(COOLDOWN_STORAGE_KEY, Date.now().toString());
    setCooldownRemaining(COOLDOWN_DURATION_MS);

    setIsGenerating(false);
    notify(
      "Stats report generation started. It may take several minutes to complete all periods.",
      { type: "success" },
    );

    // Refresh report info after a delay
    setTimeout(() => {
      fetchReportInfo();
    }, 10000);
  };

  const formatCooldownTime = (ms) => {
    const minutes = Math.ceil(ms / 60000);
    return `${minutes} min`;
  };

  const isDisabled = isGenerating || cooldownRemaining > 0;

  const getButtonLabel = () => {
    if (isGenerating) return "Starting generation...";
    if (cooldownRemaining > 0)
      return `Available in ${formatCooldownTime(cooldownRemaining)}`;
    return "Regenerate All Reports";
  };

  const reports = reportsData?.reports || [];
  const supportedPeriods = reportsData?.supportedPeriods || [30, 60, 90, 180];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Stats Report Cache
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          The stats report cache pre-computes provider statistics to improve
          dashboard performance. Reports are generated for multiple time periods
          (30, 60, 90, and 180 days). The cache is automatically regenerated
          every hour.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Current Cache Status
        </Typography>

        {loadingInfo ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Loading cache info...</Typography>
          </Box>
        ) : reports.length > 0 ? (
          <TableContainer sx={{ my: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell>Date Range</TableCell>
                  <TableCell>Records</TableCell>
                  <TableCell>Last Generated</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supportedPeriods.map((period) => {
                  const report = reports.find((r) => r.periodDays === period);
                  return (
                    <TableRow key={period}>
                      <TableCell>
                        <strong>{period} days</strong>
                      </TableCell>
                      <TableCell>
                        {report
                          ? `${report.startDate} to ${report.endDate}`
                          : "-"}
                      </TableCell>
                      <TableCell>{report?.recordCount || "-"}</TableCell>
                      <TableCell>
                        {report
                          ? formatTimestampToLocal(report.createdAt)
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {report ? (
                          <Chip
                            icon={<CheckIcon />}
                            label="Cached"
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            label="Not cached"
                            color="warning"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="error" sx={{ my: 1 }}>
            No cached reports found. Generate to improve performance.
          </Typography>
        )}

        {cooldownRemaining > 0 && (
          <Typography variant="body2" color="warning.main" sx={{ my: 1 }}>
            Report generation was recently triggered. Next generation available
            in {formatCooldownTime(cooldownRemaining)}.
          </Typography>
        )}

        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={isDisabled}
            startIcon={
              isGenerating ? <CircularProgress size={16} /> : <RefreshIcon />
            }
            sx={{ minWidth: 220 }}
          >
            {getButtonLabel()}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const AdminToolsPage = () => {
  return (
    <Box sx={{ p: 2 }}>
      <Title title="Admin Tools" />
      <Typography variant="h4" gutterBottom>
        Admin Tools
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Administrative tools for managing the Apiari platform.
      </Typography>

      <Box sx={{ maxWidth: 600 }}>
        <StatsReportCachePanel />
      </Box>
    </Box>
  );
};

export default AdminToolsPage;
